import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { map, tap } from 'rxjs/operators';
import { Observable, merge, BehaviorSubject, of } from 'rxjs';
import { MeteringPoint } from '@model';

/**
 * Data source for the InvoicesTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class MeteringpointPaneTableDataSource extends DataSource<MeteringPoint> {
  data: MeteringPoint[] = [];
  _filterChange = new BehaviorSubject('');
  get filter(): string {
    return this._filterChange.value;
  }
  set filter(filter: string) {
    this._filterChange.next(filter.trim());
  }

  _filteredColumnChange = new BehaviorSubject([]);
  get filteredColumns(): string[] {
    return this._filteredColumnChange.value;
  }
  set filteredColumns(filteredColumns: string[]) {
    this._filteredColumnChange.next(filteredColumns);
  }

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  renderedData: MeteringPoint[] = [];

  constructor(
    private paginator: MatPaginator,
    private sort: MatSort,
    private staticData: MeteringPoint[]
  ) {
    super();
    this.loadingSubject.next(true);

    of(this.staticData).subscribe(invoices => {
      this.data = invoices;
      this.data.length > 0 ? this.loadingSubject.next(false) : this.loadingSubject.next(true);
    });
    //this._filterChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  connect(): Observable<MeteringPoint[]> {
    // Set the paginators length
    this.paginator.length = this.data.length;

    return merge(of(this.staticData), this.paginator.page, this.sort.sortChange, this._filterChange).pipe(
      map(() => {
        // Filter data


        // Sort filtered data
        const sortedData = this.getSortedData(this.staticData.slice());

        // Grab the page's slice of the filtered sorted data.
        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this.paginator.pageSize);
        return this.renderedData;
      })
    );
  } 

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: MeteringPoint[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'meteringPointId':
          return compare(+a.id, +b.id, isAsc);
        case 'meteringPointName':
          return compare(a.name, b.name, isAsc);
        case 'meteringPointAddress':
          return compare(a.addresses[0].street, b.addresses[0].street, isAsc);
        case 'meteringPointGroups':
          {
            let grA = a.parentGroups?.length > 0 ? a.parentGroups[0].name: '';            
            let grB = b.parentGroups?.length > 0 ? b.parentGroups[0].name: '';
            return compare(grA, grB, isAsc);
          }   
        default:
          return 0;
      }
    });
  }

  disconnect() {}
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a, b, isAsc) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
